import type { TemplateRegistration, TemplateData } from './types';
import { sanitizeText, sanitizeSeoText, escapeAttr } from './sanitize';
import { getIconSvg, renderStars, buildFontsUrl, buildNavLinks, NavDataFlags, truncateAtSentence } from './helpers';

export const monoType: TemplateRegistration = {
  meta: {
    id: 'mono-type',
    name: 'Mono Type',
    category: 'Minimal',
    description: 'Monochrome monospace design with tight grid alignment',
    previewImage: 'https://images.unsplash.com/photo-1558618666-fcd25c85f82e?w=800&q=80',
    supportedFeatures: ['contact-form', 'phone-display', 'address-map', 'business-hours', 'about-section', 'services-list', 'testimonials', 'google-reviews', 'social-links', 'faq', 'price-list', 'promotions', 'photo-gallery'],
    defaultEnabledFeatures: ['contact-form', 'phone-display', 'about-section', 'services-list', 'testimonials'],
  },
  render(data: TemplateData, enabledFeatures: Set<string>): string {
    const safeName = sanitizeText(data.businessName);
    const safeHeadline = sanitizeText(data.headline);
    const safeAbout = sanitizeText(data.aboutText);
    const safeSeoTitle = sanitizeSeoText(data.seo.title, 70);
    const safeSeoDesc = sanitizeSeoText(data.seo.description, 160);

    const phone = data.businessData?.phone || null;
    const contactEmail = data.businessData?.contactEmail || null;
    const rating = data.businessData?.rating || null;
    const reviewCount = data.businessData?.reviewCount || 0;
    const businessHours = data.businessData?.businessHours || [];
    const photos = data.businessData?.photos || [];
    const address = data.businessData?.address || '';
    const googleMapsUrl = data.businessData?.googleMapsUrl || null;
    const galleryImages = photos.slice(1, 6);

    const navDataFlags: NavDataFlags = {
      hasServices: data.services.length > 0,
      hasTestimonials: data.testimonials.length > 0,
      hasGalleryPhotos: galleryImages.length > 0,
      hasFaq: !!(data.faq && data.faq.length > 0),
      hasPriceList: !!(data.priceList && data.priceList.length > 0),
    };
    const navLinks = buildNavLinks(enabledFeatures, navDataFlags);
    const fontsUrl = buildFontsUrl(['JetBrains Mono', 'Sora']);

    const sections: string[] = [];

    // --- Google Reviews ---
    if (enabledFeatures.has('google-reviews') && rating) {
      sections.push(`
      <section class="google-reviews">
        <div class="container">
          <div class="review-badge">
            <div class="review-stars">${renderStars(Math.round(rating))}</div>
            <span class="review-text">${rating.toFixed(1)} out of 5 &mdash; ${reviewCount} Google reviews</span>
          </div>
        </div>
      </section>`);
    }

    // --- Promotions ---
    if (enabledFeatures.has('promotions') && data.promotion) {
      const promoTitle = sanitizeText(data.promotion.title);
      const promoDesc = sanitizeText(data.promotion.description);
      const promoDiscount = sanitizeText(data.promotion.discount);
      sections.push(`
      <section class="promo-banner">
        <div class="container">
          <div class="promo-inner">
            <span class="promo-badge">${promoDiscount}</span>
            <div class="promo-content">
              <strong>${promoTitle}</strong>
              <span>${promoDesc}</span>
            </div>
          </div>
        </div>
      </section>`);
    }

    // --- About ---
    if (enabledFeatures.has('about-section')) {
      sections.push(`
      <section id="about" class="about-section-bleed">
        <div class="container">
          <h2 class="section-title">// About</h2>
          <div class="mono-rule"></div>
          <p class="about-text-bleed">${safeAbout}</p>
        </div>
      </section>`);
    }

    // --- Services ---
    if (enabledFeatures.has('services-list') && data.services.length > 0) {
      const serviceRows = data.services.map(s => {
        const sName = sanitizeText(s.name);
        const sDesc = sanitizeText(s.description);
        return `
          <div class="service-row">
            <div class="service-icon">${getIconSvg(s.icon || 'star')}</div>
            <div class="service-body">
              <h3>${sName}</h3>
              <p>${sDesc}</p>
            </div>
            ${s.price ? `<span class="service-price">${sanitizeText(s.price)}</span>` : ''}
          </div>`;
      }).join('');
      sections.push(`
      <section id="services" class="section section-alt">
        <div class="container">
          <h2 class="section-title">// Services</h2>
          <div class="mono-rule"></div>
          <div class="services-list">${serviceRows}</div>
        </div>
      </section>`);
    }

    // --- Photo Gallery ---
    if (enabledFeatures.has('photo-gallery') && galleryImages.length > 0) {
      const imgs = galleryImages.map(url => {
        return `<div class="gallery-item"><img src="${escapeAttr(url)}" alt="${safeName}" loading="lazy"></div>`;
      }).join('');
      sections.push(`
      <section id="gallery" class="section">
        <div class="container">
          <h2 class="section-title">// Gallery</h2>
          <div class="mono-rule"></div>
          <div class="gallery-grid">${imgs}</div>
        </div>
      </section>`);
    }

    // --- Testimonials ---
    if (enabledFeatures.has('testimonials') && data.testimonials.length > 0) {
      const cards = data.testimonials.map(t => {
        const tName = sanitizeText(t.name);
        const tContent = sanitizeText(t.content);
        const tRole = t.role ? sanitizeText(t.role) : '';
        return `
          <div class="testimonial-card">
            <p class="testimonial-body">&ldquo;${tContent}&rdquo;</p>
            <div class="testimonial-author">
              <strong>${tName}</strong>
              ${tRole ? `<span class="testimonial-role">${tRole}</span>` : ''}
              ${t.rating ? `<div class="testimonial-stars">${renderStars(t.rating)}</div>` : ''}
            </div>
          </div>`;
      }).join('');
      sections.push(`
      <section id="testimonials" class="section section-alt">
        <div class="container">
          <h2 class="section-title">// Testimonials</h2>
          <div class="mono-rule"></div>
          <div class="testimonials-list">${cards}</div>
        </div>
      </section>`);
    }

    // --- FAQ ---
    if (enabledFeatures.has('faq') && data.faq && data.faq.length > 0) {
      const items = data.faq.map(f => {
        const q = sanitizeText(f.question);
        const a = sanitizeText(f.answer);
        return `
          <div class="faq-item">
            <h3 class="faq-q">${q}</h3>
            <p class="faq-a">${a}</p>
          </div>`;
      }).join('');
      sections.push(`
      <section id="faq" class="section">
        <div class="container">
          <h2 class="section-title">// FAQ</h2>
          <div class="mono-rule"></div>
          <div class="faq-list">${items}</div>
        </div>
      </section>`);
    }

    // --- Price List ---
    if (enabledFeatures.has('price-list') && data.priceList && data.priceList.length > 0) {
      const rows = data.priceList.map(p => {
        const pName = sanitizeText(p.name);
        const pDesc = sanitizeText(p.description);
        const pPrice = sanitizeText(p.price);
        return `
          <div class="price-row">
            <div class="price-info">
              <h3>${pName}</h3>
              <p>${pDesc}</p>
            </div>
            <span class="price-amount">${pPrice}</span>
          </div>`;
      }).join('');
      sections.push(`
      <section id="pricing" class="section section-alt">
        <div class="container">
          <h2 class="section-title">// Pricing</h2>
          <div class="mono-rule"></div>
          <div class="price-list">${rows}</div>
        </div>
      </section>`);
    }

    // --- Business Hours ---
    if (enabledFeatures.has('business-hours') && businessHours.length > 0) {
      const hoursList = businessHours.map(h => `<li>${sanitizeText(h)}</li>`).join('');
      sections.push(`
      <section class="section">
        <div class="container">
          <h2 class="section-title">// Hours</h2>
          <div class="mono-rule"></div>
          <ul class="hours-list">${hoursList}</ul>
        </div>
      </section>`);
    }

    // --- Contact ---
    if (enabledFeatures.has('contact-form')) {
      sections.push(`
      <section id="contact" class="section section-alt">
        <div class="container">
          <h2 class="section-title">// Contact</h2>
          <div class="mono-rule"></div>
          <div class="contact-info">
            ${enabledFeatures.has('phone-display') && phone ? `<div class="contact-item">${getIconSvg('phone')}<a href="tel:${escapeAttr(phone)}">${sanitizeText(phone)}</a></div>` : ''}
            ${enabledFeatures.has('address-map') && address ? `<div class="contact-item">${getIconSvg('map-pin')}<span>${sanitizeText(address)}</span></div>` : ''}
            ${enabledFeatures.has('address-map') && googleMapsUrl ? `<a class="map-link" href="${escapeAttr(googleMapsUrl)}" target="_blank" rel="noopener noreferrer">View on Google Maps</a>` : ''}
          </div>
          <form class="contact-form" ${contactEmail ? `action="https://formsubmit.co/${escapeAttr(contactEmail)}" method="POST"` : 'onsubmit="return false;"'}>
            <input type="text" placeholder="Your Name" required>
            <input type="email" placeholder="Your Email" required>
            <textarea placeholder="Message" rows="4" required></textarea>
            <button type="submit">Send</button>
          </form>
        </div>
      </section>`);
    }

    return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${safeSeoTitle}</title>
  <meta name="description" content="${safeSeoDesc}">
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link href="${fontsUrl}" rel="stylesheet">
  <style>
    *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }
    :root {
      --primary: #000000; --bg: #FFFFFF; --bg-alt: #F5F5F5; --accent: #000000;
      --text: #000000; --text-muted: #666666;
      --font-heading: 'JetBrains Mono', monospace; --font-body: 'Sora', sans-serif;
    }
    html { scroll-behavior: smooth; }
    body { font-family: var(--font-body); background: var(--bg); color: var(--text); line-height: 1.6; }
    .container { max-width: 720px; margin: 0 auto; padding: 0 24px; }
    a { color: var(--text); text-decoration: none; transition: opacity .2s; }
    a:hover { opacity: .7; }

    /* MONO RULE */
    .mono-rule { width: 100%; height: 1px; background: var(--text); margin-bottom: 32px; }

    /* NAV */
    .navbar { position: fixed; top: 0; left: 0; right: 0; z-index: 100; background: var(--bg); border-bottom: 2px solid var(--text); }
    .nav-inner { display: flex; align-items: center; justify-content: space-between; padding: 14px 24px; max-width: 720px; margin: 0 auto; }
    .nav-brand { font-family: var(--font-heading); font-size: 1.1rem; color: var(--text); font-weight: 700; text-transform: uppercase; letter-spacing: 2px; }
    .nav-links { display: flex; gap: 24px; list-style: none; }
    .nav-links a { color: var(--text-muted); font-family: var(--font-heading); font-size: .75rem; text-transform: uppercase; letter-spacing: 2px; font-weight: 500; }
    .nav-links a:hover { color: var(--text); }
    .nav-toggle { display: none; background: none; border: none; cursor: pointer; }
    .nav-toggle span { display: block; width: 22px; height: 2px; background: var(--text); margin: 5px 0; transition: .3s; }

    /* HERO - brutalist, thick borders, no rounded corners */
    .hero { position: relative; min-height: 100vh; display: flex; align-items: center; overflow: hidden; background: var(--bg); border-bottom: 4px solid var(--text); }
    .hero-content { position: relative; z-index: 2; padding: 140px 24px 100px; max-width: 720px; margin: 0 auto; width: 100%; }
    .hero-box { border: 4px solid var(--text); padding: 48px 40px; border-radius: 0; }
    .hero h1 { font-family: var(--font-heading); font-size: clamp(2rem, 5vw, 3.4rem); line-height: 1.2; margin-bottom: 20px; font-weight: 700; color: var(--text); text-transform: uppercase; letter-spacing: -1px; border-radius: 0; }
    .hero-sub { font-size: 1rem; color: var(--text-muted); max-width: 560px; margin-bottom: 40px; line-height: 1.7; }
    .hero-cta { display: inline-block; padding: 12px 32px; background: var(--text); color: var(--bg); font-family: var(--font-heading); font-size: .8rem; font-weight: 600; text-transform: uppercase; letter-spacing: 2px; border: 4px solid var(--text); border-radius: 0; cursor: pointer; transition: background .2s, color .2s; }
    .hero-cta:hover { background: var(--bg); color: var(--text); opacity: 1; }

    /* SECTIONS */
    .section { padding: 72px 0; }
    .section-alt { background: var(--bg-alt); }
    .section-title { font-family: var(--font-heading); font-size: clamp(1rem, 2.5vw, 1.3rem); font-weight: 700; margin-bottom: 12px; color: var(--text); text-transform: uppercase; letter-spacing: 2px; }

    /* ABOUT - full-bleed with stark bg overlay */
    .about-section-bleed { background: var(--text); padding: 72px 0; }
    .about-section-bleed .section-title { color: var(--bg); }
    .about-section-bleed .mono-rule { background: var(--bg); }
    .about-text-bleed { font-size: 1rem; color: #CCCCCC; max-width: 720px; line-height: 1.8; }

    /* SERVICES - single-col full-width rows with thick top border */
    .services-list { display: flex; flex-direction: column; }
    .service-row { display: flex; gap: 16px; align-items: flex-start; padding: 24px 0; border-top: 4px solid var(--text); }
    .service-row:last-child { border-bottom: 4px solid var(--text); }
    .service-icon { display: none; }
    .icon-char { font-size: 20px; line-height: 1; color: var(--text); }
    .service-body { flex: 1; }
    .service-body h3 { font-family: var(--font-heading); font-size: 1.1rem; font-weight: 700; margin-bottom: 4px; color: var(--text); text-transform: uppercase; letter-spacing: 1px; }
    .service-body p { color: var(--text-muted); font-size: .9rem; line-height: 1.6; }
    .service-price { font-family: var(--font-heading); font-weight: 700; font-size: 1.1rem; color: var(--text); white-space: nowrap; }

    /* GALLERY */
    .gallery-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 2px; }
    .gallery-item img { width: 100%; height: 200px; object-fit: cover; display: block; }

    /* TESTIMONIALS - stacked alternating left/right */
    .testimonials-list { display: flex; flex-direction: column; gap: 24px; }
    .testimonial-card { padding: 28px; border: 4px solid var(--text); border-radius: 0; max-width: 80%; }
    .testimonial-card:nth-child(odd) { align-self: flex-start; }
    .testimonial-card:nth-child(even) { align-self: flex-end; }
    .testimonial-body { font-size: .95rem; font-style: italic; color: var(--text-muted); line-height: 1.7; margin-bottom: 12px; }
    .testimonial-author strong { display: block; font-family: var(--font-heading); color: var(--text); font-size: .85rem; text-transform: uppercase; letter-spacing: 1px; }
    .testimonial-role { color: var(--text-muted); font-size: .8rem; }
    .testimonial-stars { margin-top: 6px; }
    .star { color: #CCCCCC; font-size: 1rem; }
    .star.filled { color: var(--text); }

    /* FAQ */
    .faq-list { max-width: 720px; }
    .faq-item { padding: 20px 0; border-bottom: 1px solid #CCCCCC; }
    .faq-q { font-family: var(--font-heading); font-size: .9rem; font-weight: 600; margin-bottom: 8px; color: var(--text); text-transform: uppercase; letter-spacing: 1px; }
    .faq-a { color: var(--text-muted); line-height: 1.7; font-size: .95rem; }

    /* PRICE LIST */
    .price-list { max-width: 720px; }
    .price-row { display: flex; justify-content: space-between; align-items: flex-start; padding: 20px 0; border-bottom: 1px solid #CCCCCC; gap: 24px; }
    .price-info h3 { font-family: var(--font-heading); font-size: .9rem; font-weight: 600; margin-bottom: 4px; color: var(--text); text-transform: uppercase; letter-spacing: 1px; }
    .price-info p { color: var(--text-muted); font-size: .85rem; }
    .price-amount { font-family: var(--font-heading); font-size: 1.2rem; color: var(--text); white-space: nowrap; font-weight: 700; }

    /* HOURS */
    .hours-list { list-style: none; max-width: 400px; }
    .hours-list li { padding: 10px 0; border-bottom: 1px solid #CCCCCC; color: var(--text-muted); font-size: .9rem; font-family: var(--font-heading); }

    /* GOOGLE REVIEWS */
    .google-reviews { background: var(--text); padding: 14px 0; }
    .review-badge { display: flex; align-items: center; justify-content: center; gap: 12px; flex-wrap: wrap; }
    .review-badge .star, .review-badge .star.filled { color: #fff; font-size: 1.1rem; }
    .review-text { color: #fff; font-family: var(--font-heading); font-weight: 600; font-size: .8rem; letter-spacing: 1px; text-transform: uppercase; }

    /* PROMO */
    .promo-banner { background: var(--bg); padding: 20px 0; border-bottom: 2px solid var(--text); }
    .promo-inner { display: flex; align-items: center; gap: 20px; flex-wrap: wrap; }
    .promo-badge { background: var(--text); color: var(--bg); font-family: var(--font-heading); padding: 6px 16px; font-size: .8rem; font-weight: 700; letter-spacing: 1px; text-transform: uppercase; }
    .promo-content strong { display: block; margin-bottom: 2px; color: var(--text); font-family: var(--font-heading); font-size: .9rem; }
    .promo-content span { color: var(--text-muted); font-size: .85rem; }

    /* CONTACT - full-width form, thick borders, no decorations */
    .contact-info { display: flex; flex-direction: column; gap: 16px; margin-bottom: 32px; }
    .contact-item { display: flex; align-items: center; gap: 12px; color: var(--text-muted); font-size: .9rem; }
    .contact-item .icon-char { color: var(--text); flex-shrink: 0; font-size: 18px; line-height: 1; }
    .contact-item a { color: var(--text); }
    .map-link { display: inline-block; margin-top: 8px; font-family: var(--font-heading); font-weight: 600; text-transform: uppercase; font-size: .75rem; letter-spacing: 2px; color: var(--text); }
    .contact-form { display: flex; flex-direction: column; gap: 12px; max-width: 100%; }
    .contact-form input, .contact-form textarea { background: var(--bg); border: 4px solid var(--text); border-radius: 0; color: var(--text); padding: 14px 16px; font-family: var(--font-body); font-size: .9rem; outline: none; transition: border-color .2s; }
    .contact-form input:focus, .contact-form textarea:focus { border-color: var(--text-muted); }
    .contact-form button { padding: 14px; background: var(--text); color: var(--bg); font-family: var(--font-heading); font-size: .8rem; font-weight: 700; text-transform: uppercase; letter-spacing: 2px; border: 4px solid var(--text); border-radius: 0; cursor: pointer; transition: background .2s, color .2s; }
    .contact-form button:hover { background: var(--bg); color: var(--text); }

    /* FOOTER */
    .footer { background: var(--text); padding: 36px 0; text-align: center; border-top: 2px solid var(--text); }
    .footer p { color: #999999; font-family: var(--font-heading); font-size: .75rem; letter-spacing: 1px; text-transform: uppercase; }

    /* RESPONSIVE */
    @media (max-width: 768px) {
      .nav-links { display: none; flex-direction: column; position: absolute; top: 100%; left: 0; right: 0; background: var(--bg); border-bottom: 2px solid var(--text); padding: 16px 24px; gap: 14px; }
      .nav-links.show { display: flex; }
      .nav-toggle { display: block; }
      .gallery-grid { grid-template-columns: repeat(2, 1fr); }
      .service-row { flex-direction: column; }
      .testimonial-card { max-width: 100%; }
      .price-row { flex-direction: column; gap: 8px; }
    }
    @media (max-width: 480px) {
      .gallery-grid { grid-template-columns: 1fr; }
      .hero-content { padding: 110px 16px 60px; }
    }
  </style>
</head>
<body>
  <nav class="navbar">
    <div class="nav-inner">
      <a href="#" class="nav-brand">${safeName}</a>
      <button class="nav-toggle" aria-label="Toggle menu" onclick="this.parentElement.querySelector('.nav-links').classList.toggle('show')">
        <span></span><span></span><span></span>
      </button>
      <ul class="nav-links">
        ${navLinks.map(l => `<li><a href="${l.href}">${l.label}</a></li>`).join('')}
      </ul>
    </div>
  </nav>

  <section class="hero">
    <div class="hero-content">
      <div class="hero-box">
        <h1>${safeHeadline}</h1>
        <p class="hero-sub">${truncateAtSentence(safeAbout)}</p>
        ${enabledFeatures.has('contact-form') ? `<a href="#contact" class="hero-cta">Contact Us</a>` : ''}
      </div>
    </div>
  </section>

  ${sections.join('\n')}

  <footer class="footer">
    <div class="container">
      <p>${safeName}</p>
    </div>
  </footer>
</body>
</html>`;
  }
};
